import { Box, Grid, MenuItem, TextField } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import {
  getEmptyTestamentAsset,
  IAsset,
} from '../../../../../Interfaces/Asset/IAsset';
import { IAssetCategoryId } from '../../../../../Interfaces/Asset/IAssetCategory';
import { IAssetTypeId } from '../../../../../Interfaces/Asset/IAssetType';
import { isInheritanceProvisionRelationship } from '../../../../../Interfaces/ILegalTransactionRelationship/Relationships/InheritanceProvision/ILegalTransactionRelationshipInheritanceProvision';
import { ILegalTransactionRelationshipMember } from '../../../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipMember';
import { ILegalTransactionRelationship } from '../../../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import { randomNumber } from '../../../../../utils';
import { DialogResponsive } from '../../../../core/DialogResponsive';
import { legalClientStore } from '../../generic/legalClientStore';
import { NavRow } from '../../generic/NavRow';
import { present } from '../../generic/utilities';
import { TestamentAssetsView } from './TestamentAssetView';
import {
  IAssetProvisions,
  ILegalClientAssetProvision,
  TestamentProvisionsView,
} from './TestamentProvisionsView';

const assetTypeOptions = {
  [IAssetCategoryId.PersonalProperty]: [
    { idAssetType: IAssetTypeId.BankAccount, label: 'Bankkonto' },
    { idAssetType: IAssetTypeId.Cash, label: 'Bargeld' },
    { idAssetType: IAssetTypeId.Depot, label: 'Depot' },
  ],
  [IAssetCategoryId.Liabilities]: [
    { idAssetType: IAssetTypeId.Loan, label: 'Kredit' },
    { idAssetType: IAssetTypeId.Mortgage, label: 'Hypothek' },
  ],
  [IAssetCategoryId.RealEstate]: [
    { idAssetType: IAssetTypeId.RealEstate, label: 'Immobilie' },
  ],
  [IAssetCategoryId.Other]: [
    { idAssetType: IAssetTypeId.Other, label: 'Sonstiges' },
  ],
};

export type ITestamentAssetOwnerAndProvisions = {
  asset: IAsset;
  ownerIds: number[];
  ownerShares: { [key: number]: number };
  provisions: IAssetProvisions;
};

type TestamentAssetsDialogProps = {
  assets: IAsset[];
  testators: ILegalTransactionRelationshipMember[];
  relationshipArray: ILegalTransactionRelationship[];
  assetCategoryId: IAssetCategoryId | null;
  initialAssetTypeId?: IAssetTypeId | null;
  assetId?: number | null;
  realEstates: IAsset[];
  onSave: (
    testamentAssetOwnerAndProvisions: ITestamentAssetOwnerAndProvisions
  ) => void;
  onClose: () => void;
};

const buildOwnersOptions = (
  testators: ILegalTransactionRelationshipMember[]
): { id: number; name: string; joint?: boolean }[] => {
  return [
    ...testators.map(testator => ({
      id: testator.idLegalClient,
      name: `${testator.FirstName} ${testator.LastName}`,
    })),
    testators.length > 1
      ? {
          id: 0,
          name: `${testators[0].FirstName} ${testators[0].LastName} & ${testators[1].FirstName} ${testators[1].LastName}`,
          joint: true,
        }
      : null,
  ].filter(Boolean) as { id: number; name: string }[];
};

function getAssetOwnerId(
  asset: IAsset,
  testators: ILegalTransactionRelationshipMember[]
) {
  const owners = testators.filter(testatorLegalClient =>
    legalClientStore
      .getState()
      .getLegalClient(testatorLegalClient.idLegalClient)
      ?.AssetArray.some(assetItem => assetItem.idAsset === asset.idAsset)
  );

  if (owners.length === 1) {
    return owners[0].idLegalClient;
  }

  if (owners.length > 1) {
    return 0;
  }

  return null;
}

function getAssetProvisions(
  relationshipArray: ILegalTransactionRelationship[],
  asset: IAsset | null
): IAssetProvisions {
  if (!asset) {
    return {
      legalClientProvisions: [],
      otherProvisionsNotes: '',
    };
  }

  const inheritanceProvisionRelationship = relationshipArray.find(
    relationship =>
      isInheritanceProvisionRelationship(relationship) &&
      relationship.AssetArray.some(
        relAsset => relAsset.idAsset === asset.idAsset
      )
  );

  if (isInheritanceProvisionRelationship(inheritanceProvisionRelationship)) {
    const legalClientProvisions = inheritanceProvisionRelationship.Members.map(
      member => {
        return {
          id: randomNumber(),
          idLegalClient: member.idLegalClient,
          provisionNote: member.data.provisionNotes || '',
        } satisfies ILegalClientAssetProvision;
      }
    ).filter(present);

    return {
      legalClientProvisions,
      otherProvisionsNotes:
        inheritanceProvisionRelationship.data.provisionNotes || '',
    };
  }

  return {
    legalClientProvisions: [],
    otherProvisionsNotes: '',
  };
}

function getAssetOwnershipShares(
  testators: ILegalTransactionRelationshipMember[],
  asset: IAsset
): { [key: number]: number } {
  const ownerShares: { [key: number]: number } = {};

  testators.forEach(member => {
    const legalClient = legalClientStore
      .getState()
      .getLegalClient(member.idLegalClient);

    const legalClientAsset = legalClient?.AssetArray.find(
      la => la.idAsset === asset.idAsset
    );

    if (legalClientAsset) {
      ownerShares[member.idLegalClient] = legalClientAsset.share;
    }
  });

  return ownerShares;
}

export function TestamentAssetsDialog({
  assets,
  testators,
  relationshipArray,
  assetCategoryId,
  realEstates,
  initialAssetTypeId,
  assetId,
  onClose,
  onSave,
}: TestamentAssetsDialogProps) {
  const [assetType, setAssetType] = useState<IAssetTypeId | null>(
    assetCategoryId === IAssetCategoryId.Other
      ? IAssetTypeId.Other
      : initialAssetTypeId || null
  );
  const [asset, setAsset] = useState<IAsset | null>(
    assetId ? assets.find(a => a.idAsset === assetId) || null : null
  );
  const [selectedOwnerId, setSelectedOwnerId] = useState<number | null>(
    asset ? getAssetOwnerId(asset, testators) : null
  );
  const [provisions, setProvisions] = useState<IAssetProvisions>(
    getAssetProvisions(relationshipArray, asset)
  );
  const [ownerShares, setOwnerShares] = useState<{ [key: number]: number }>(
    asset ? getAssetOwnershipShares(testators, asset) : {}
  );

  const ownersOptions = buildOwnersOptions(testators);

  useEffect(() => {
    if (assetType && !assetId) {
      const newAsset = getEmptyTestamentAsset(assetType);
      setAsset(newAsset);
    }
  }, [assetType, assetId]);

  useEffect(() => {
    if (asset) {
      if (selectedOwnerId === 0) {
        if (
          Object.values(ownerShares).length !== 2 ||
          Object.values(ownerShares).reduce((a, b) => a + b, 0) !== 100
        ) {
          const equalShare = 100 / testators.length;
          const shares: { [key: number]: number } = {};
          if (ownerShares)
            testators.forEach(testator => {
              shares[testator.idLegalClient] = equalShare;
            });
          setOwnerShares(shares);
        }
      } else {
        setOwnerShares({
          [selectedOwnerId as number]: 100,
        });
      }
    }
  }, [selectedOwnerId, testators, setOwnerShares]);

  const onSaveLocal = useCallback(() => {
    const owner = ownersOptions.find(
      testator => testator.id === selectedOwnerId
    );

    if (asset && owner) {
      onSave({
        asset,
        ownerIds: owner.joint
          ? testators.map(t => t.idLegalClient)
          : [owner.id],
        ownerShares,
        provisions,
      });
    }

    onClose();
  }, [
    asset,
    selectedOwnerId,
    testators,
    onClose,
    provisions,
    onSave,
    ownersOptions,
  ]);

  if (!assetCategoryId) {
    return null;
  }

  return (
    <Box>
      <DialogResponsive
        open={true}
        onClose={onClose}
        maxWidth='lg'
        title='Vermögensgegenstand hinzufügen'
      >
        <Grid container spacing={2}>
          {assetCategoryId !== IAssetCategoryId.Other && (
            <Grid item xs={12}>
              <TextField
                label='Art des Vermögensgegenstandes'
                select
                value={assetType}
                onChange={e =>
                  setAssetType(Number(e.target.value) as IAssetTypeId)
                }
                size='small'
                fullWidth
                InputLabelProps={{ shrink: !!assetType }}
              >
                {assetTypeOptions[assetCategoryId].map(
                  ({ idAssetType, label }) => (
                    <MenuItem key={idAssetType} value={idAssetType}>
                      {label}
                    </MenuItem>
                  )
                )}
              </TextField>
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              label='Name des Vermögensgegenstandes'
              type='text'
              value={asset ? asset.name : ''}
              onChange={e =>
                setAsset(prev =>
                  prev ? { ...prev, name: e.target.value } : prev
                )
              }
              size='small'
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label='Eigentümer des Vermögensgegenstandes'
              select
              value={selectedOwnerId}
              onChange={e => setSelectedOwnerId(Number(e.target.value))}
              size='small'
              fullWidth
            >
              {buildOwnersOptions(testators).map(({ id, name }) => (
                <MenuItem key={id} value={id}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {selectedOwnerId === 0 && (
            <>
              <Grid item xs={6}>
                <TextField
                  label={`Anteil ${testators[0].FirstName} ${testators[0].LastName} (%)`}
                  type='number'
                  value={ownerShares[testators[0].idLegalClient] || ''}
                  onChange={e =>
                    setOwnerShares(prev => ({
                      ...prev,
                      [testators[0].idLegalClient]: Number(e.target.value),
                      [testators[1].idLegalClient]:
                        100 - Number(e.target.value),
                    }))
                  }
                  size='small'
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={`Anteil ${testators[1].FirstName} ${testators[1].LastName} (%)`}
                  type='number'
                  value={ownerShares[testators[1].idLegalClient] || ''}
                  onChange={e =>
                    setOwnerShares(prev => ({
                      ...prev,
                      [testators[1].idLegalClient]: Number(e.target.value),
                      [testators[0].idLegalClient]:
                        100 - Number(e.target.value),
                    }))
                  }
                  size='small'
                  fullWidth
                />
              </Grid>
            </>
          )}

          {asset && (
            <>
              <Grid spacing={2} item xs={12}>
                <TestamentAssetsView
                  asset={asset}
                  setAsset={setAsset}
                  realEstates={realEstates}
                />
              </Grid>
              <Grid spacing={2} item xs={12}>
                <TestamentProvisionsView
                  provisions={provisions}
                  onProvisionsChange={setProvisions}
                  testatorArray={testators}
                />
              </Grid>
            </>
          )}

          <NavRow
            handleNext={onSaveLocal}
            handleBack={onClose}
            backButtonText='Abbrechen'
            nextButtonText='Übernehmen'
          />
        </Grid>
      </DialogResponsive>
    </Box>
  );
}
